import { inject } from '@angular/core';
import { RedirectFunction, Route } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { RoleEnum } from 'app/core/roles/role.types';
import { LayoutComponent } from 'app/layout/layout.component';

/**
 * Where the app opens: administrators on the dashboard, instructors on their registers.
 *
 * Roles are read from the access token, which is in local storage both straight after sign-in and
 * after a reload. Expiry is deliberately not checked here - AuthGuard owns that through
 * AuthService.check(), and sending an expired session to sign-in from here would loop with
 * NoAuthGuard, which sends an authenticated user back to ''.
 */
const landingRedirect: RedirectFunction = () => {
    const token = inject(AuthService).accessToken;

    if (!token) {
        return '/sign-in';
    }

    let roles: string[];

    try {
        roles = AuthUtils.getTokenRoles(token);
    } catch {
        return '/sign-in';
    }

    if (roles.includes(RoleEnum.Role.Administrator)) {
        return '/dashboard';
    }

    if (roles.includes(RoleEnum.Role.Instructor)) {
        return '/attendance';
    }

    return '/no-access';
};

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    { path: '', pathMatch: 'full', redirectTo: landingRedirect },

    // After signing in, the sign-in page navigates here, so it lands on the same page as ''.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: landingRedirect,
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'no-access',
                loadChildren: () =>
                    import('app/modules/auth/no-access/no-access.routes'),
            },
        ],
    },

    // Public application form (no guard, no chrome) – embeddable in an iframe
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'apply',
                loadChildren: () =>
                    import('app/modules/application/application.routes'),
            },
        ],
    },

    // The application itself. One group, so moving between screens never rebuilds the layout.
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        children: [
            {
                path: 'dashboard',
                loadChildren: () =>
                    import('app/modules/dashboard/dashboard.routes'),
            },
            {
                path: 'instructors',
                loadChildren: () =>
                    import('app/modules/instructors/instructor.routes'),
            },
            {
                path: 'class-groups',
                loadChildren: () =>
                    import('app/modules/class-groups/class-group.routes'),
            },
            {
                path: 'schools',
                loadChildren: () => import('app/modules/schools/school.routes'),
            },
            {
                path: 'students',
                loadChildren: () =>
                    import('app/modules/students/student.routes'),
            },
            {
                path: 'calendar-exceptions',
                loadChildren: () =>
                    import(
                        'app/modules/calendar-exceptions/calendar-exception.routes'
                    ),
            },
            {
                path: 'lessons',
                loadChildren: () => import('app/modules/lessons/lesson.routes'),
            },
            {
                path: 'attendance',
                loadChildren: () =>
                    import('app/modules/attendance/attendance.routes'),
            },
            {
                path: 'registers',
                loadChildren: () =>
                    import('app/modules/registers/register.routes'),
            },
            {
                path: 'progress-reports',
                loadChildren: () =>
                    import(
                        'app/modules/progress-reports/progress-report.routes'
                    ),
            },
            {
                path: 'finance',
                loadChildren: () =>
                    import('app/modules/finance/finance.routes'),
            },
            {
                path: 'users',
                loadChildren: () => import('app/modules/users/user.routes'),
            },
        ],
    },

    // Anything else - a stale bookmark, a removed page - goes back through the landing rule.
    { path: '**', redirectTo: '' },
];
